import type { CacheRequestConfig } from '../cache/axios.ts';
import { Header } from '../header/headers.ts';
import type { MaybePromise } from '../util/types.ts';
import type {
  AxiosStorage,
  CachedResponse,
  CachedStorageValue,
  StaleStorageValue,
  StorageValue
} from './types.ts';

/**
 * Returns true if the provided object was created from {@link buildStorage} function.
 *
 * @deprecated This function will be hidden in future versions. Please tell us why you need it at https://github.com/arthurfiorette/axios-cache-interceptor/issues/1158
 */
export const isStorage = (obj: unknown): obj is AxiosStorage =>
  !!obj && !!(obj as Record<string, boolean>)['is-storage'];

/**
 * Migrates old header-based revalidation data to new meta.revalidation format.
 * This ensures backward compatibility with existing cache entries.
 *
 * @deprecated Internal migration function. Will be removed when all cache entries
 * have naturally expired and been recreated with new format.
 */
function migrateRevalidationHeaders(data: CachedResponse): void {
  // Skip if already has meta.revalidation
  if (data.meta?.revalidation) {
    return;
  }

  const oldEtag = data.headers[Header.XAxiosCacheEtag];
  const oldLastModified = data.headers[Header.XAxiosCacheLastModified];

  if (oldEtag || oldLastModified) {
    data.meta ??= {};
    data.meta.revalidation = {};

    if (oldEtag) {
      data.meta.revalidation.etag = oldEtag;
    }

    if (oldLastModified) {
      data.meta.revalidation.lastModified =
        oldLastModified === 'use-cache-timestamp' ? true : oldLastModified;
    }

    delete data.headers[Header.XAxiosCacheEtag];
    delete data.headers[Header.XAxiosCacheLastModified];
    delete data.headers[Header.XAxiosCacheStaleIfError];
  }
}

function hasRevalidationMetadata(value: CachedStorageValue | StaleStorageValue): boolean {
  // Migrate old entries on-the-fly
  migrateRevalidationHeaders(value.data);

  const headers = value.data.headers;
  const revalidation = value.data.meta?.revalidation;

  return (
    // Standard HTTP revalidation headers
    Header.ETag in headers ||
    Header.LastModified in headers ||
    // Revalidation metadata (new format)
    !!(revalidation?.etag || revalidation?.lastModified)
  );
}

/** Returns true if value must be revalidated */
export function mustRevalidate(value: CachedStorageValue | StaleStorageValue): boolean {
  // Must revalidate is a special case and should not serve stale values
  // We could use cache-control's parse function, but this is way faster and simpler
  return String(value.data.headers[Header.CacheControl]).includes('must-revalidate');
}

/** Returns true if this has sufficient properties to stale instead of expire. */
export function canStale(value: CachedStorageValue): boolean {
  if (hasRevalidationMetadata(value)) {
    return true;
  }

  return (
    value.state === 'cached' &&
    value.staleTtl !== undefined &&
    // Only allow stale values after the ttl is already in the past and the staleTtl is in the future.
    // In cases that just createdAt + ttl > Date.now(), isn't enough because the staleTtl could be <= 0.
    // This logic only returns true when Date.now() is between the (createdAt + ttl) and (createdAt + ttl + staleTtl).
    // Following the example below:
    // |--createdAt--:--ttl--:---staleTtl--->
    // [        past        ][now is in here]
    Math.abs(Date.now() - (value.createdAt + value.ttl)) <= value.staleTtl
  );
}

/**
 * Checks if the provided cache is expired. You should also check if the cache
 * {@link canStale} and {@link mayUseStale}
 */
export function isExpired(value: CachedStorageValue | StaleStorageValue): boolean {
  return value.ttl !== undefined && value.createdAt + value.ttl <= Date.now();
}

/**
 * Defines which storage states are evicted first when cleaning up the storage.
 */
const StateEvictionOrder: Record<StorageValue['state'], number> = {
  empty: 0,
  'must-revalidate': 1,
  stale: 2,
  cached: 3,
  // loading states usually don't have any data and are the most important ones
  // to keep around
  loading: 4
};

/**
 * Is a comparator function that sorts storage entries by their eviction priority
 * and, in the same group, by older first.
 */
export function storageEntriesSorter(
  [, a]: [string, StorageValue],
  [, b]: [string, StorageValue]
): number {
  const stateDiff = StateEvictionOrder[a.state] - StateEvictionOrder[b.state];
  if (stateDiff !== 0) return stateDiff;
  return (a.createdAt || 0) - (b.createdAt || 0);
}

/**
 * Returns true if the storage entry can be removed according to its state and the
 * provided maxStaleAge.
 */
export function canRemoveStorageEntry(value: StorageValue, maxStaleAge: number): boolean {
  switch (value.state) {
    case 'loading':
      return false;

    case 'empty':
    case 'must-revalidate':
      return true;

    case 'cached':
      return isExpired(value) && !canStale(value);

    case 'stale':
      if (maxStaleAge !== undefined && value.ttl !== undefined) {
        return Date.now() > value.createdAt + value.ttl + maxStaleAge;
      }
      return false;
  }
}

export interface BuildStorage extends Omit<AxiosStorage, 'get'> {
  /**
   * Returns the value for the given key. This method does not have to make checks for
   * cache invalidation or anything. It just returns what was previous saved, if present.
   *
   * @param key The key to look for
   * @param currentRequest The current {@link CacheRequestConfig}, if any
   * @see https://axios-cache-interceptor.js.org/guide/storages#buildstorage
   */
  find: (
    key: string,
    currentRequest?: CacheRequestConfig
  ) => MaybePromise<StorageValue | undefined>;
}

/**
 * All integrated storages are wrappers around the `buildStorage` function. External
 * libraries use it and if you want to build your own, `buildStorage` is the way to go!
 *
 * The exported `buildStorage` function abstracts the storage interface and requires a
 * super simple object to build the storage.
 *
 * **Note**: You can only create custom storages with this function.
 *
 * @example
 *
 * ```js
 * const myStorage = buildStorage({
 *   find: () => {...},
 *   set: () => {...},
 *   remove: () => {...},
 *   clear: () => {...}
 * });
 *
 * const axios = setupCache(axios, { storage: myStorage });
 * ```
 *
 * @see https://axios-cache-interceptor.js.org/guide/storages#buildstorage
 */
export function buildStorage({ set, find, remove, clear }: BuildStorage): AxiosStorage {
  return {
    //@ts-expect-error - we don't want to expose this
    'is-storage': 1,
    set,
    remove,
    clear,
    get: async (key, config) => {
      let value = await find(key, config);

      if (!value) {
        return { state: 'empty' };
      }

      if (
        value.state === 'empty' ||
        value.state === 'loading' ||
        value.state === 'must-revalidate'
      ) {
        return value;
      }

      // Migrate old x-axios-cache headers to meta.revalidation on-the-fly
      if (value.state === 'cached' || value.state === 'stale') {
        migrateRevalidationHeaders(value.data);
      }

      // Handle cached values
      if (value.state === 'cached') {
        if (!isExpired(value)) {
          return value;
        }

        // Tries to stale expired value
        if (!canStale(value)) {
          await remove(key, config);
          return { state: 'empty' };
        }

        value = {
          state: 'stale',
          createdAt: value.createdAt,
          data: value.data,
          ttl: value.staleTtl !== undefined ? value.staleTtl + value.ttl : undefined
        };

        await set(key, value, config);

        // Must revalidate is a special case and should not serve stale values
        if (mustRevalidate(value)) {
          return { ...value, state: 'must-revalidate' };
        }
      }

      // A second check in case the new stale value was created already expired.
      if (!isExpired(value)) {
        return value;
      }

      if (hasRevalidationMetadata(value)) {
        return value;
      }

      await remove(key, config);
      return { state: 'empty' };
    }
  };
}
